import {
  AGE_BAND_OPTIONS,
  CAPABILITY_LEVELS,
  EDT_ACTIVITY_OPTIONS,
  PROCEDURES_ALPHABETICAL,
  TIMES_OF_DAY,
} from '@iculogbook/shared';
import type { AgeBand, CapabilityLevel, EdtActivity, TimeOfDay } from '@iculogbook/shared';

import { Button, ChipGroup, Field, Sheet, TextInput } from '../../components/index.ts';
import { fromDateTimeLocalValue, toDateTimeLocalValue } from '../log/datetime.ts';
import type { EntryFilters } from './model.ts';
import { NO_FILTERS, activeFilterCount } from './model.ts';

const KIND_OPTIONS = [
  { value: 'case' as const, label: 'Cases' },
  { value: 'procedure' as const, label: 'Procedures' },
  { value: 'edt' as const, label: 'EDT / Teaching' },
  { value: 'anaesthetic' as const, label: 'Anaesthetics' },
  { value: 'communication' as const, label: 'Communication' },
];

const TIME_LABELS: Record<TimeOfDay, string> = { day: 'Day', night: 'Night' };

/** A date input holds a day; the filter holds the moment that day starts or ends. */
function toDayValue(value: Date | null): string {
  return value === null ? '' : toDateTimeLocalValue(value).slice(0, 10);
}

function fromDayValue(value: string, edge: 'start' | 'end'): Date | null {
  if (value === '') return null;
  return fromDateTimeLocalValue(`${value}T${edge === 'start' ? '00:00' : '23:59'}`);
}

export function EntryFilterSheet({
  open,
  filters,
  onChange,
  onClose,
  /** Only the procedures this trainee has actually logged. */
  usedProcedureKeys,
  /** Likewise for EDT — the block is hidden entirely until there is one. */
  usedActivities,
}: {
  open: boolean;
  filters: EntryFilters;
  onChange: (filters: EntryFilters) => void;
  onClose: () => void;
  usedProcedureKeys: readonly string[];
  usedActivities: readonly EdtActivity[];
}) {
  function set<K extends keyof EntryFilters>(key: K, value: EntryFilters[K]): void {
    onChange({ ...filters, [key]: value });
  }

  const procedureOptions = PROCEDURES_ALPHABETICAL.filter((procedure) =>
    usedProcedureKeys.includes(procedure.key),
  ).map((procedure) => ({ value: procedure.key, label: procedure.label }));

  const activityOptions = EDT_ACTIVITY_OPTIONS.filter((option) =>
    usedActivities.includes(option.key),
  ).map((option) => ({ value: option.key, label: option.label }));

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Filters"
      footer={
        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={() => onChange({ ...NO_FILTERS, query: filters.query })}
            disabled={activeFilterCount(filters) === 0}
          >
            Clear all
          </Button>
          <Button variant="primary" block onClick={onClose}>
            Show results
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-5">
        <div className="flex gap-3">
          <Field label="From" className="flex-1">
            <TextInput
              type="date"
              value={toDayValue(filters.from)}
              onChange={(event) => set('from', fromDayValue(event.target.value, 'start'))}
            />
          </Field>
          <Field label="To" className="flex-1">
            <TextInput
              type="date"
              value={toDayValue(filters.to)}
              onChange={(event) => set('to', fromDayValue(event.target.value, 'end'))}
            />
          </Field>
        </div>

        <FilterBlock label="Entry type">
          <ChipGroup
            label="Entry type"
            multiple
            options={KIND_OPTIONS}
            value={filters.kinds}
            onChange={(next) => set('kinds', next)}
          />
        </FilterBlock>

        <FilterBlock label="Capability level">
          <ChipGroup
            label="Capability level"
            multiple
            options={CAPABILITY_LEVELS.map((level) => ({
              value: String(level),
              label: `Level ${String(level)}`,
            }))}
            value={filters.capabilityLevels.map(String)}
            onChange={(next) =>
              set(
                'capabilityLevels',
                next
                  .map(Number)
                  .filter((level): level is CapabilityLevel =>
                    CAPABILITY_LEVELS.includes(level as CapabilityLevel),
                  ),
              )
            }
          />
        </FilterBlock>

        <FilterBlock label="Age band">
          <ChipGroup
            label="Age band"
            multiple
            options={AGE_BAND_OPTIONS.map((option) => ({ value: option.key, label: option.label }))}
            value={filters.ageBands}
            onChange={(next) => set('ageBands', next as AgeBand[])}
          />
        </FilterBlock>

        <FilterBlock label="When">
          <div className="flex flex-wrap gap-2">
            <ChipGroup
              label="Time of day"
              multiple
              options={TIMES_OF_DAY.map((time) => ({ value: time, label: TIME_LABELS[time] }))}
              value={filters.timesOfDay}
              onChange={(next) => set('timesOfDay', next as TimeOfDay[])}
            />
            <ChipGroup
              label="Weekend"
              multiple
              options={[{ value: 'weekend', label: 'Weekend only' }]}
              value={filters.weekendOnly ? ['weekend'] : []}
              onChange={(next) => set('weekendOnly', next.length > 0)}
            />
          </div>
        </FilterBlock>

        {procedureOptions.length === 0 ? null : (
          <FilterBlock label="Procedure">
            <ChipGroup
              label="Procedure"
              multiple
              options={procedureOptions}
              value={filters.procedureKeys}
              onChange={(next) => set('procedureKeys', next)}
            />
          </FilterBlock>
        )}

        {activityOptions.length === 0 ? null : (
          <FilterBlock label="EDT activity">
            <ChipGroup
              label="EDT activity"
              multiple
              options={activityOptions}
              value={filters.activities}
              onChange={(next) => set('activities', next as EdtActivity[])}
            />
          </FilterBlock>
        )}
      </div>
    </Sheet>
  );
}

function FilterBlock({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-medium text-text">{label}</span>
      {children}
    </div>
  );
}
